import Box from "@mui/material/Box";
import { FC, useMemo, useState, useRef } from "react";
import { BarChartColors } from "../../../../../../components/charts/constants";
import SimpleColumnChart from "../../../../../../components/charts/SimpleColumnChart/SimpleColumnChart";
import { PeriodSelect } from "../../../../../../components/filters/PeriodSelect";
import { useProductTrendingData } from "../../../MarketLandscape/hooks/useTrendingProductData";
import { getChartData } from "./utils";
import useMatchProductsById from "../../../MarketLandscape/hooks/useMatchProductsById";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";
import { determineChartContent, hasNoData } from "../../../../utils";
import { useCombinedFilters } from "../../../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { ChartExportMenu } from "../../../../../../components/charts/ChartExportMenu";
import { TitleWithTooltipInfo } from "../../../../../../components/charts/TitleWithTooltipInfo";
import { CHART_CONSTANTS } from "../../../constants";

const { chartTitle, tooltip, baseId, csvFieldMappings, defaultSelectedYear } =
  CHART_CONSTANTS.competitorNewProductDetections;

export const CompetitorNewProductDetections: FC = () => {
  const [isChartHovered, setIsChartHovered] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(defaultSelectedYear);
  const chartRef = useRef<am4charts.XYChart>(null);
  const combinedFilters = useCombinedFilters();
  const competitorsProductIds = useWorkspaceStore(
    (state) => state.draft?.configuration.competitorsProductIds || []
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { productsNameMapById } = useMatchProductsById(competitorsProductIds);

  const { marketTimeSeriesProductBreakdown, loading: isLoadingTrendingData } =
    useProductTrendingData({
      companyFilters: combinedFilters || {},
      ids: competitorsProductIds,
      lookBackYears: Number.parseInt(selectedPeriod),
      localizeInstalls,
    });

  const chartData = useMemo(() => {
    return getChartData(marketTimeSeriesProductBreakdown, productsNameMapById);
  }, [marketTimeSeriesProductBreakdown, productsNameMapById]);

  const noDataReturned = useMemo(
    () => hasNoData(isLoadingTrendingData, chartData),
    [chartData, isLoadingTrendingData]
  );

  const shouldShowExport =
    !noDataReturned && chartRef && !isLoadingTrendingData;

  const chartContent = useMemo(() => {
    return determineChartContent({
      productIds: competitorsProductIds,
      isLoading: isLoadingTrendingData,
      noDataReturned,
      baseId,
      chartToRender: (
        <Box id={`${baseId}-chart-wrapper`} height="364px">
          <SimpleColumnChart
            id={`${baseId}-chart`}
            data={chartData}
            xAxisDataPoint="productName"
            xAxisTooltipFormat={"{productName}"}
            yAxisDataPoint="hits"
            columnTooltipFormat={"{productName}\nNew Detections: [bold]{hits}"}
            xAxisLabel="Competitors"
            yAxisLabel="New Product Detections"
            barColor={BarChartColors}
            truncateXAxisLabels
            ref={chartRef}
          />
        </Box>
      ),
    });
  }, [chartData, noDataReturned, isLoadingTrendingData, competitorsProductIds]);

  return (
    <Box
      id={`${baseId}-wrapper`}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="390px"
      borderRadius="10px"
      padding="22.3px 20px 19px"
      border="solid 1px #edeff1"
      gap="25px"
      bgcolor={
        noDataReturned || competitorsProductIds.length === 0
          ? "#edeff1"
          : "#fff"
      }
      onMouseEnter={() => setIsChartHovered(true)}
      onMouseLeave={() => setIsChartHovered(false)}
    >
      <Box
        id={`${baseId}-header`}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <TitleWithTooltipInfo
          tooltipTitle={tooltip}
          chartTitle={chartTitle}
          showInfoIcon={isChartHovered}
        />
        {shouldShowExport && (
          <Box display="flex" gap={2} alignItems="center">
            <PeriodSelect
              id={`${baseId}-period-select`}
              selectedPeriod={selectedPeriod}
              onChange={setSelectedPeriod}
            />

            <ChartExportMenu
              chart={chartRef}
              title="Competitor New Product Detections"
              csvFieldMappings={csvFieldMappings}
            />
          </Box>
        )}
      </Box>
      {chartContent}
    </Box>
  );
};
